import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ImageIcon from '@mui/icons-material/Image'

// Hook Imports
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useGlobalStoreActions, useGlobalStoreData } from '../../../../../../src/store/globalStore'

// Service Imports
import { deleteEmailSignature, uploadEmailSignature } from '../../../services/B2BServices'
import { getUserDetailsAndProcess } from '../../../../../services/DataServiceMethods'

const EmailSignaturesTabContent = () => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useToast()

  const user = useGlobalStoreData.user()
  const { setUserData } = useGlobalStoreActions()

  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [signatureToDelete, setSignatureToDelete] = useState(null)

  const signatures = user?.emailSignatureFiles || []

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      await uploadEmailSignature(user?.ownedB2bAccountId, file)
      showSuccess(t('mailTemplate.users.signatureUploaded') || 'Signature uploaded successfully.')

      await getUserDetailsAndProcess(true, user, setUserData, true)
    } catch (error) {
      showError(t('mailTemplate.users.signatureUploadFailed') || 'Upload failed.')
    } finally {
      setIsUploading(false)

      event.target.value = ''
    }
  }

  const confirmDelete = (attachmentId) => {
    setSignatureToDelete(attachmentId)
    setDeleteConfirmOpen(true)
  }

  const handleDelete = async () => {
    if (!signatureToDelete) return

    setIsDeleting(signatureToDelete)
    setDeleteConfirmOpen(false)
    try {
      await deleteEmailSignature(user?.ownedB2bAccountId, signatureToDelete)
      showSuccess(t('mailTemplate.users.signatureDeleted') || 'Signature deleted successfully.')
      await getUserDetailsAndProcess(true, user, setUserData, true)
    } catch (error) {
      showError(t('mailTemplate.users.signatureDeleteFailed') || 'Delete failed.')
    } finally {
      setIsDeleting(null)
      setSignatureToDelete(null)
    }
  }

  const formatFileSize = (base64String) => {
    if (!base64String) return ''
    const sizeInBytes = (base64String.length * 3) / 4
    if (sizeInBytes < 1024) return `${sizeInBytes.toFixed(0)} B`
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const getImageSrc = (attachment) => {
    if (attachment.fileBytes) {
      return `data:${attachment.contentType || 'image/png'};base64,${attachment.fileBytes}`
    }
    return attachment.url || ''
  }

  return (
    <Box sx={{ p: 3, minHeight: '25rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h6" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
            {t('mailTemplate.users.emailSignaturesTab') || 'Email Signatures'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mailTemplate.users.supportedFormats') || 'Supported formats: JPG, PNG, GIF'}
          </Typography>
        </Box>
        <Button
          component="label"
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={20} color="inherit"/> : <CloudUploadIcon/>}
          disabled={isUploading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {t('mailTemplate.users.uploadSignature') || 'Upload Image'}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileUpload}
          />
        </Button>
      </Box>

      {signatures.length === 0
        ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'background.default'
            }}
          >
            <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}/>
            <Typography variant="body1" color="text.secondary">
              {t('mailTemplate.users.noSignatures') || 'No signatures uploaded yet.'}
            </Typography>
          </Box>
          )
        : (
          <Grid container spacing={3}>
            {signatures.map((attachment) => {
              const imgSrc = getImageSrc(attachment)
              return (
                <Grid item xs={12} sm={6} md={4} key={attachment.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      {imgSrc
                        ? (
                          <img
                            src={imgSrc}
                            alt={attachment.fileName}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '16px' }}
                          />
                          )
                        : (
                          <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }}/>
                          )}
                    </Box>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: '16px !important' }}>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={attachment.fileName}>
                          {attachment.fileName || `Signature ${attachment.id.substring(0, 8)}`}
                        </Typography>
                        {attachment.fileBytes && (
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(attachment.fileBytes)}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={t('mailTemplate.users.deleteSignature') || 'Delete'}>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => confirmDelete(attachment.id)}
                          disabled={isDeleting === attachment.id}
                        >
                          {isDeleting === attachment.id
                            ? (
                              <CircularProgress size={20} color="error"/>
                              )
                            : (
                              <DeleteOutlineIcon/>
                              )}
                        </IconButton>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
          )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t('mailTemplate.users.deleteSignatureTab') || 'Delete Signature'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('mailTemplate.users.deleteSignatureConfirm') || 'Are you sure you want to delete this signature?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('mailTemplate.campaigns.modal.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            {t('common.delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmailSignaturesTabContent
